export const MIME_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
export const MAX_DURATION = 30;

export const getSupportedMime = () =>
  MIME_TYPES.find((m) => MediaRecorder.isTypeSupported(m)) || '';

export const formatTime = (sec) => {
  const m = Math.floor(sec / 60);
  const s = (sec % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

const WAVEFORM_BAR_COUNT = 58;

export const seedBars = (src) => {
  const seed = (src || '').split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return Array.from({ length: WAVEFORM_BAR_COUNT }, (_, i) => {
    const x = Math.sin(seed + i * 2.4) * 0.5 + 0.5;
    return 0.15 + x * 0.72;
  });
};

export const extractPeaks = async (src) => {
  const arrayBuffer = await fetch(src).then((r) => r.arrayBuffer());
  const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  const decoded = await audioCtx.decodeAudioData(arrayBuffer);
  audioCtx.close();

  const raw = decoded.getChannelData(0);
  const blockSize = Math.floor(raw.length / WAVEFORM_BAR_COUNT);
  const peaks = [];
  let max = 0;

  for (let i = 0; i < WAVEFORM_BAR_COUNT; i++) {
    let sum = 0;
    for (let j = 0; j < blockSize; j++) {
      sum += Math.abs(raw[i * blockSize + j] || 0);
    }
    const avg = sum / blockSize;
    peaks.push(avg);
    if (avg > max) max = avg;
  }

  return peaks.map((v) => 0.08 + (max > 0 ? (v / max) * 0.84 : 0.1));
};

export const cropAudio = async (src, startRatio, endRatio) => {
  const arrayBuffer = await fetch(src).then((r) => r.arrayBuffer());
  const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  const decoded = await audioCtx.decodeAudioData(arrayBuffer);
  audioCtx.close();

  // Use decoded.length (sample count) not decoded.duration — WebM recordings often have
  // inaccurate duration metadata which breaks time-based offset calculations
  const totalSamples = decoded.length;
  const startSample = Math.floor(startRatio * totalSamples);
  const endSample = Math.ceil(endRatio * totalSamples);
  const length = Math.max(1, endSample - startSample);

  const srcData = decoded.getChannelData(0);
  const croppedData = srcData.slice(startSample, endSample);
  const fakeBuffer = {
    sampleRate: decoded.sampleRate,
    getChannelData: () => croppedData,
  };

  return encodeWav(fakeBuffer);
};

export const encodeWav = (audioBuffer) => {
  const sampleRate = audioBuffer.sampleRate;
  const samples = audioBuffer.getChannelData(0);
  const dataLength = samples.length * 2;
  const buf = new ArrayBuffer(44 + dataLength);
  const view = new DataView(buf);
  const str = (off, s) => { for (let i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i)); };
  str(0, 'RIFF'); view.setUint32(4, 36 + dataLength, true);
  str(8, 'WAVE'); str(12, 'fmt '); view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true); view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true); view.setUint16(34, 16, true);
  str(36, 'data'); view.setUint32(40, dataLength, true);
  let off = 44;
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
    off += 2;
  }
  return buf;
};
